import { GetOrderItem_getOrders } from '_graphql/queries/__generated__/GetOrderItem';
import { ActionButton, Paginator, TableComponent, TableLoader } from 'components';
import { usePagination } from 'hooks';
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import { useProductOrders } from '../hooks/use-product-orders';
import { useNavigate } from 'react-location';

type Props = {
  sku?: string | null;
};

const ProductOrdersTab = ({ sku }: Props) => {
  const navigate = useNavigate();
  const { offset, limit, setPage } = usePagination();
  const { orders, count, refetch, loading } = useProductOrders({
    variables: {
      filter: {
        sku: {
          eq: sku,
        },
      },
      pagination: {
        limit,
        offset,
      },
    },
    skip: !sku,
    notifyOnNetworkStatusChange: true,
    fetchPolicy: 'network-only',
  });

  return (
    <div className="flex-1 w-full px-4 sm:px-6 py-4 sm:py-6">
      <TableComponent
        data={orders}
        title="Product Orders"
        refetch={refetch}
        columns={[
          {
            name: 'Order Number',
            accessor: 'orderNumber',
          },
          {
            name: 'Cart Ref#',
            accessor: 'order',
            render: (item: GetOrderItem_getOrders) => (
              <div className="flex flex-col">
                <p className="text-sm font-medium text-gray-700">
                  {item.order?.cart_number || 'N/A'}
                </p>
                <p className="text-xs font-medium text-gray-500">
                  {item.createdAt
                    ? moment(item.createdAt).format('Do MMM, YYYY')
                    : 'N/A'}
                </p>
              </div>
            ),
          },
          {
            name: 'Customer',
            accessor: 'order',
            render: (item: GetOrderItem_getOrders) => (
              <div className="flex flex-col">
                <p className="text-sm font-medium text-gray-700">
                  {item.order?.customer?.name || 'N/A'}
                </p>
                <p className="text-xs font-medium text-gray-500">
                  {item.order?.customer?.phone || 'N/A'}
                </p>
              </div>
            ),
          },
          {
            name: 'Quantity',
            accessor: 'quantity',
          },
          {
            name: 'Price',
            accessor: 'price',
            render: (item: GetOrderItem_getOrders) =>
              numeral(item.price).format('#,#.##'),
          },
          {
            name: 'Status',
            accessor: 'supplier_status',
            render: (item: GetOrderItem_getOrders) => (
              <span className="text-xs rounded-full bg-slate-100 text-center px-2 py-1">
                {item.supplier_status?.toUpperCase() || 'N/A'}
              </span>
            ),
          },
          {
            name: 'Payment',
            accessor: 'order',
            render: (item: GetOrderItem_getOrders) => (
              <div className="flex flex-col">
                <p className="text-sm font-medium text-gray-700">
                  {_.startCase(item.order?.payment_type) || 'N/A'}
                </p>
                <span className="text-xs font-medium text-gray-500">
                  {item.order?.payment_status || 'N/A'}
                </span>
              </div>
            ),
          },
          {
            name: 'Action',
            accessor: '',
            render: (item: GetOrderItem_getOrders) => (
              <ActionButton
                tooltip="View Order"
                action="view"
                onClick={() =>
                  navigate({
                    to: `/orders/${item.order_id}`,
                  })
                }
              />
            ),
          },
        ]}
        hasSearch
        loading={loading}
        renderLoader={() => <TableLoader items={[1, 1, 1, 1, 1, 1, 1, 1]} />}
        pagination={() => (
          <Paginator
            offset={offset}
            limit={limit}
            totalItems={count}
            currentSize={orders.length}
            setOffset={setPage}
          />
        )}
      />
    </div>
  );
};

export default ProductOrdersTab;
